import { useDeferredValue, useMemo, useRef, useState } from 'react'
import { hasErrors, importText, normalizeForSearch } from '../../domain'
import { readFileText } from '../../platform/download'
import { getDb } from '../../storage/db'
import { newSongDocument } from '../../storage/factory'
import { createSong, listSongs } from '../../storage/repository'
import common from '../common.module.css'
import { useLive } from '../hooks/useLive'
import { Sheet } from '../play/Sheet'
import { usePreferences } from '../prefs'
import { navigate, paths } from '../router'
import { t } from '../strings'
import styles from './NewSong.module.css'

/** Cuántos avisos se listan; el resto se resume. */
const MAX_ISSUES = 6

const sameText = (a: string, b: string) =>
  normalizeForSearch(a).trim() === normalizeForSearch(b).trim()

export function NewSong() {
  const preferences = usePreferences()
  const [title, setTitle] = useState('')
  const [artist, setArtist] = useState('')
  const [source, setSource] = useState('')
  const [text, setText] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const fileInput = useRef<HTMLInputElement>(null)

  const deferredText = useDeferredValue(text)
  const imported = useMemo(
    () => (deferredText.trim() === '' ? null : importText(deferredText, { source })),
    [deferredText, source],
  )
  const { data: songs } = useLive(() => listSongs(getDb()), [])

  const finalTitle = title.trim() || imported?.title?.trim() || ''
  const finalArtist = artist.trim() || imported?.artist?.trim() || ''
  const duplicate =
    finalTitle !== '' &&
    (songs ?? []).some(
      (song) => sameText(song.title, finalTitle) && sameText(song.artist, finalArtist),
    )

  const blocking = imported ? hasErrors(imported.issues) : true
  const canSave = !saving && finalTitle !== '' && imported !== null && !blocking

  const stats = useMemo(() => {
    if (!imported) return null
    const lines = imported.content.sections.flatMap((section) => section.lines)
    return {
      sections: imported.content.sections.length,
      lines: lines.filter((line) => line.kind !== 'comment').length,
      chords: lines.reduce((total, line) => total + line.chords.length, 0),
    }
  }, [imported])

  const n = t.newSong
  const issues = imported?.issues ?? []

  async function save() {
    if (!imported || !canSave) return
    setSaving(true)
    setError(null)
    try {
      const now = new Date().toISOString()
      const content = {
        ...imported.content,
        provenance: {
          ...imported.content.provenance,
          content: { ...imported.content.provenance.content!, at: now },
        },
      }
      const document = newSongDocument({
        title: finalTitle,
        artist: finalArtist,
        content,
        now,
        author: 'import',
        sourceUrl: source,
      })
      await createSong(getDb(), document, now)
      navigate(paths.play(document.arrangement.id))
    } catch (cause) {
      setError(n.saveError(cause instanceof Error ? cause.message : String(cause)))
      setSaving(false)
    }
  }

  return (
    <div className={common.page}>
      <h1 className={common.pageTitle}>{n.title}</h1>
      <p className={common.lead}>{n.lead}</p>

      <div className={styles.grid}>
        <label className={common.field}>
          <span>{n.songTitle}</span>
          <input
            value={title}
            placeholder={imported?.title ?? ''}
            autoCapitalize="sentences"
            onChange={(event) => setTitle(event.target.value)}
          />
        </label>
        <label className={common.field}>
          <span>{n.songArtist}</span>
          <input
            value={artist}
            placeholder={imported?.artist ?? ''}
            onChange={(event) => setArtist(event.target.value)}
          />
        </label>
        <label className={`${common.field} ${styles.full}`}>
          <span>{n.source}</span>
          <input
            type="url"
            inputMode="url"
            value={source}
            placeholder={n.sourcePlaceholder}
            autoCapitalize="off"
            autoCorrect="off"
            spellCheck={false}
            onChange={(event) => setSource(event.target.value)}
          />
        </label>
      </div>

      <div className={common.row} style={{ marginTop: 12 }}>
        <label htmlFor="song-text" className={common.muted}>
          {n.text}
        </label>
        <button className={common.spacer} onClick={() => fileInput.current?.click()}>
          {n.openFile}
        </button>
        <span className={`${common.muted} ${common.small}`}>{n.fileHint}</span>
        <input
          ref={fileInput}
          className={styles.file}
          type="file"
          accept=".cho,.chordpro,.chopro,.txt,text/plain"
          onChange={(event) => {
            const file = event.target.files?.[0]
            event.target.value = ''
            if (file) void readFileText(file).then(setText)
          }}
        />
      </div>
      <textarea
        id="song-text"
        className={styles.paste}
        wrap="off"
        value={text}
        placeholder={n.textPlaceholder}
        autoCapitalize="off"
        autoCorrect="off"
        spellCheck={false}
        onChange={(event) => setText(event.target.value)}
      />

      {imported && stats && (
        <>
          <div className={styles.summary}>
            <strong>{imported.format === 'chordpro' ? n.formatChordpro : n.formatSheet}</strong>
            <span>{n.summary(stats.sections, stats.lines, stats.chords)}</span>
            <span>
              {n.notation(
                imported.content.notation === 'latin' ? n.notationLatin : n.notationAmerican,
              )}
            </span>
          </div>
          {issues.length > 0 && (
            <ul className={styles.issues} aria-label={n.issues}>
              {issues.slice(0, MAX_ISSUES).map((issue, index) => (
                <li key={index}>{issue.message}</li>
              ))}
              {issues.length > MAX_ISSUES && <li>… y {issues.length - MAX_ISSUES} más</li>}
            </ul>
          )}
          <h2 className={common.small}>{n.preview}</h2>
          <div className={styles.preview}>
            <Sheet content={imported.content} notation={preferences.notation} />
          </div>
        </>
      )}

      {duplicate && <p className={common.noticeWarn}>{n.duplicate}</p>}
      {title.trim() === '' && imported?.title && (
        <p className={`${common.muted} ${common.small}`}>{n.fallbackTitle(imported.title)}</p>
      )}
      {error && (
        <p className={common.noticeError} role="alert">
          {error}
        </p>
      )}

      <div className={styles.actions}>
        <button className="primary" disabled={!canSave} onClick={() => void save()}>
          {saving ? n.saving : n.save}
        </button>
        {finalTitle === '' && imported && <span className={common.muted}>{n.needTitle}</span>}
        <span className={`${common.muted} ${common.small}`}>{n.privacy}</span>
      </div>
    </div>
  )
}
